import React from 'react';
import styled from 'styled-components';
import Section, { SectionTone } from './ui/Section';
import Container from './ui/Container';
import Badge from './ui/Badge';
import Button from './ui/Button';
import DeviceFrame from './ui/DeviceFrame';
import { Eyebrow } from './ui/SectionHeading';
import { ArrowUp, ArrowUpRight, CheckIcon } from './ui/Icons';
import BookConsultationButton from './BookConsultationButton';
import type { Project } from '../data/projects';

const Header = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1.5rem 2rem;
  align-items: end;
  margin-bottom: clamp(2rem, 4vw, 3rem);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  object-fit: cover;
  background: #fff;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: var(--fs-h2);
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const Meta = styled.p`
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  color: var(--text-2);
  font-size: 0.95rem;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const PendingNote = styled.p`
  color: var(--text-3);
  font-size: var(--fs-small);
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.3fr);
  gap: clamp(1rem, 2.5vw, 2rem);
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.42fr);
  }
`;

const Extra = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(1rem, 2.5vw, 2rem);
  margin-top: clamp(1rem, 2.5vw, 2rem);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Caption = styled.p`
  margin-top: 1rem;
  font-size: var(--fs-small);
  color: var(--text-3);
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: clamp(2rem, 5vw, 4rem);
  margin-top: clamp(2.5rem, 5vw, 4rem);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Overview = styled.p`
  font-size: clamp(1.0625rem, 1.4vw, 1.1875rem);
  color: var(--text-1);
  line-height: 1.65;
`;

const BuiltTitle = styled.h3`
  font-family: var(--font-body);
  font-size: var(--fs-eyebrow);
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-3);
  margin: 2rem 0 1rem;
`;

const BuiltList = styled.ul`
  display: grid;
  gap: 0.85rem;

  li {
    display: flex;
    gap: 0.75rem;
    color: var(--text-2);
    font-size: 0.975rem;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.3rem;
    color: var(--accent);
  }

  strong {
    color: var(--text-1);
    font-weight: 600;
  }
`;

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Panel = styled.div`
  padding: 1.5rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);

  h3 {
    font-family: var(--font-body);
    font-size: var(--fs-eyebrow);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-3);
    margin-bottom: 0.85rem;
  }

  p {
    color: var(--text-2);
    font-size: 0.95rem;
  }
`;

const Chips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  li {
    padding: 0.3rem 0.65rem;
    border-radius: 6px;
    font-size: 0.8rem;
    color: var(--text-1);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border);
  }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1rem;
  font-size: 0.95rem;

  dt {
    color: var(--text-3);
  }

  dd {
    color: var(--text-1);
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: clamp(2.5rem, 5vw, 3.5rem);
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
`;

const BackToTop = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 44px;
  color: var(--text-2);
  font-size: var(--fs-small);
  font-weight: 500;

  &:hover {
    color: var(--text-1);
  }
`;

interface CaseStudyProps {
  project: Project;
  index: number;
  tone: SectionTone;
}

const CaseStudy: React.FC<CaseStudyProps> = ({ project, index, tone }) => {
  const { desktop, mobile, gallery = [] } = project.images;
  const isLive = project.status === 'live' && project.url;

  return (
    <Section $tone={tone} id={project.slug} aria-labelledby={`${project.slug}-title`}>
      <Container>
        <Header>
          <div>
            <Eyebrow>
              Case study {String(index + 1).padStart(2, '0')} · {project.kind}
            </Eyebrow>
            <TitleRow>
              {project.logo && (
                <Logo src={project.logo.src} alt={project.logo.alt} width={56} height={56} />
              )}
              <Title id={`${project.slug}-title`}>{project.name}</Title>
            </TitleRow>
            <Meta>
              <Badge variant={project.status} />
              <span>{project.location}</span>
              <span aria-hidden="true">·</span>
              <span>{project.languages}</span>
            </Meta>
          </div>
          <HeaderActions>
            {isLive ? (
              <Button href={project.url as string} variant="secondary">
                Visit {project.urlLabel} <ArrowUpRight />
                <span className="visually-hidden">(opens in a new tab)</span>
              </Button>
            ) : (
              <PendingNote>Launching soon · No public URL yet</PendingNote>
            )}
          </HeaderActions>
        </Header>

        <Gallery>
          <DeviceFrame
            src={desktop.src}
            alt={desktop.alt}
            width={desktop.width}
            height={desktop.height}
            sizes="(min-width: 1200px) 900px, 75vw"
          />
          <DeviceFrame
            variant="phone"
            src={mobile.src}
            alt={mobile.alt}
            width={mobile.width}
            height={mobile.height}
            sizes="(min-width: 1200px) 280px, 25vw"
          />
        </Gallery>
        {gallery.length > 0 && (
          <Extra>
            {gallery.map((image) => (
              <DeviceFrame
                key={image.src}
                src={image.src}
                alt={image.alt}
                width={image.width}
                height={image.height}
                sizes="(min-width: 1200px) 580px, 50vw"
              />
            ))}
          </Extra>
        )}
        {project.galleryCaption && <Caption>{project.galleryCaption}</Caption>}

        <Body>
          <div>
            <Overview>{project.overview}</Overview>
            <BuiltTitle>What we built</BuiltTitle>
            <BuiltList>
              {project.built.map((item) => (
                <li key={item.title}>
                  <CheckIcon />
                  <span>
                    <strong>{item.title}:</strong> {item.detail}
                  </span>
                </li>
              ))}
            </BuiltList>
          </div>

          <Aside>
            <Panel>
              <h3>At a glance</h3>
              <Facts>
                <dt>Client</dt>
                <dd>{project.name}</dd>
                <dt>Location</dt>
                <dd>{project.location}</dd>
                <dt>Language</dt>
                <dd>{project.languages}</dd>
                <dt>Status</dt>
                <dd>{project.status === 'live' ? 'Live' : 'Coming soon'}</dd>
              </Facts>
            </Panel>
            <Panel>
              <h3>Tech stack</h3>
              <Chips>
                {project.stack.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Chips>
            </Panel>
            <Panel>
              <h3>Status</h3>
              <p>{project.statusNote}</p>
            </Panel>
          </Aside>
        </Body>

        <Footer>
          <BookConsultationButton label="Plan a similar project" variant="secondary" />
          <BackToTop href="#top">
            <ArrowUp /> Back to top
          </BackToTop>
        </Footer>
      </Container>
    </Section>
  );
};

export default CaseStudy;
